import React from 'react';

// ─────────────────────────────────────────────────────────────────
// Sortable column headers — shared by Finance (Invoices / Receipts / Credit Notes /
// Quotations) and CRM (Leads list), so sorting looks and behaves the same everywhere.
// Click a header to sort, click again to flip the direction; `sortType` on each column
// config decides how values compare (date / number / string), `get` reads a derived value.
// ─────────────────────────────────────────────────────────────────
export function sortValueFor(row, col) {
  const raw = col.get ? col.get(row) : row[col.key];
  if (raw == null || raw === '') return null;
  if (col.sortType === 'date') {
    const t = new Date(raw).getTime();
    return isNaN(t) ? null : t;
  }
  if (col.sortType === 'number') {
    const n = Number(raw);
    return isNaN(n) ? null : n;
  }
  return String(raw).trim().toLowerCase();
}

export function applySort(rows, columns, sortKey, sortDir) {
  if (!sortKey) return rows;
  const col = columns.find(c => c.key === sortKey);
  if (!col) return rows;
  const sorted = [...rows].sort((a, b) => {
    const va = sortValueFor(a, col), vb = sortValueFor(b, col);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;   // nulls/blank sort to the end regardless of direction
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return va - vb;
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
  });
  return sortDir === 'desc' ? sorted.reverse() : sorted;
}

export function makeSortToggle(sortKey, setSortKey, setSortDir) {
  return (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };
}

// Pass `resizeId` (a short per-table name, e.g. "receipts") to let the user drag
// the right edge of a header to set that column's width, Excel-style. Widths are
// remembered per browser; double-click the edge to reset that column.
const MIN_COL_W = 60;
const widthKey = (resizeId, key) => `colw:${resizeId}:${key}`;
const readWidth = (resizeId, key) => {
  try { const v = Number(localStorage.getItem(widthKey(resizeId, key))); return v >= MIN_COL_W ? v : null; } catch { return null; }
};

export function SortableTh({ label, sortKey: key, activeKey, dir, onSort, align = 'left', style, resizeId }) {
  const isActive = activeKey === key;
  const thRef = React.useRef(null);
  const [width, setWidth] = React.useState(() => (resizeId ? readWidth(resizeId, key) : null));

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = thRef.current ? thRef.current.getBoundingClientRect().width : (width || 120);
    let latest = startW;
    const onMove = (ev) => { latest = Math.max(MIN_COL_W, Math.round(startW + ev.clientX - startX)); setWidth(latest); };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      try { localStorage.setItem(widthKey(resizeId, key), String(latest)); } catch { /* ignore */ }
      window.dispatchEvent(new CustomEvent('colw-changed', { detail: { resizeId } }));
    };
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  const resetWidth = (e) => {
    e.stopPropagation();
    setWidth(null);
    try { localStorage.removeItem(widthKey(resizeId, key)); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('colw-changed', { detail: { resizeId } }));
  };

  return (
    <th ref={thRef} onClick={() => onSort(key)} title="Click to sort" style={{
      padding: '10px 16px', textAlign: align, fontSize: 11, fontWeight: 700, color: '#fff',
      textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', cursor: 'pointer',
      userSelect: 'none', position: 'relative', ...style,
      ...(width ? { width, minWidth: width, maxWidth: width, boxSizing: 'border-box' } : null),
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        {label}
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, lineHeight: 0 }}>
          <svg width="7" height="5" viewBox="0 0 8 6"><polygon points="4,0 8,6 0,6" fill={isActive && dir === 'asc' ? '#fff' : 'rgba(255,255,255,0.4)'} /></svg>
          <svg width="7" height="5" viewBox="0 0 8 6"><polygon points="0,0 8,0 4,6" fill={isActive && dir === 'desc' ? '#fff' : 'rgba(255,255,255,0.4)'} /></svg>
        </span>
      </span>
      {resizeId && (
        <span onMouseDown={startResize} onDoubleClick={resetWidth} onClick={(e) => e.stopPropagation()}
          title="Drag to resize · double-click to reset"
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 8, cursor: 'col-resize', zIndex: 2,
            borderRight: '2px solid rgba(255,255,255,0.18)' }} />
      )}
    </th>
  );
}
